// /opt/isp_erp/frontend/src/pages/admin/finanzas/CuentasBancarias.jsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, Chip, Alert, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { finanzasAPI } from '../../../api/finanzas/finanzas.api';

const CuentasBancarias = () => {
  const [cuentas, setCuentas] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCuenta, setSelectedCuenta] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    banco: '',
    tipo_cuenta: 'AHORROS',
    numero_cuenta: '',
    saldo_inicial: 0,
    activa: true,
    principal: false,
    sucursal_id: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [cuentasRes, sucursalesRes] = await Promise.all([
        finanzasAPI.getCuentasBancarias(),
        finanzasAPI.getSucursales()
      ]);
      setCuentas(cuentasRes.data);
      setSucursales(sucursalesRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (cuenta = null) => {
    if (cuenta) {
      setEditMode(true);
      setSelectedCuenta(cuenta);
      setFormData({
        nombre: cuenta.nombre,
        banco: cuenta.banco,
        tipo_cuenta: cuenta.tipo_cuenta,
        numero_cuenta: cuenta.numero_cuenta,
        saldo_inicial: cuenta.saldo_inicial,
        activa: cuenta.activa,
        principal: cuenta.principal,
        sucursal_id: cuenta.sucursal_id || ''
      });
    } else {
      setEditMode(false);
      setSelectedCuenta(null);
      setFormData({
        nombre: '',
        banco: '',
        tipo_cuenta: 'AHORROS',
        numero_cuenta: '',
        saldo_inicial: 0,
        activa: true,
        principal: false,
        sucursal_id: ''
      });
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await finanzasAPI.actualizarCuentaBancaria(selectedCuenta.id, formData);
      } else {
        await finanzasAPI.crearCuentaBancaria(formData);
      }
      setOpenDialog(false);
      cargarDatos();
    } catch (err) {
      alert('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta cuenta bancaria?')) {
      try {
        await finanzasAPI.eliminarCuentaBancaria(id);
        cargarDatos();
      } catch (err) {
        alert('Error al eliminar');
      }
    }
  };

  if (loading) {
    return <CircularProgress sx={{ m: 4 }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>🏦 Cuentas Bancarias</Typography>
      
      <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ mb: 2 }}>
        Nueva Cuenta
      </Button>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Banco</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Saldo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cuentas.map(c => (
              <TableRow key={c.id}>
                <TableCell>{c.banco}</TableCell>
                <TableCell>{c.numero_cuenta}</TableCell>
                <TableCell>{c.tipo_cuenta}</TableCell>
                <TableCell>{c.sucursal_nombre || '-'}</TableCell>
                <TableCell>${c.saldo_actual?.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip label={c.activa ? 'Activa' : 'Inactiva'} color={c.activa ? 'success' : 'default'} size="small" />
                  {c.principal && <Chip label="Principal" color="primary" size="small" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(c)}><EditIcon /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Editar Cuenta' : 'Nueva Cuenta'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Nombre" name="nombre" value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} fullWidth required />
            <TextField label="Banco" name="banco" value={formData.banco} onChange={(e) => setFormData({...formData, banco: e.target.value})} fullWidth required />
            <FormControl fullWidth>
              <InputLabel>Tipo de Cuenta</InputLabel>
              <Select value={formData.tipo_cuenta} onChange={(e) => setFormData({...formData, tipo_cuenta: e.target.value})} label="Tipo de Cuenta">
                <MenuItem value="AHORROS">Ahorros</MenuItem>
                <MenuItem value="CORRIENTE">Corriente</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Número de Cuenta" name="numero_cuenta" value={formData.numero_cuenta} onChange={(e) => setFormData({...formData, numero_cuenta: e.target.value})} fullWidth required />
            <FormControl fullWidth>
              <InputLabel>Sucursal</InputLabel>
              <Select value={formData.sucursal_id} onChange={(e) => setFormData({...formData, sucursal_id: e.target.value})} label="Sucursal">
                <MenuItem value="">Sin sucursal</MenuItem>
                {sucursales.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Saldo Inicial" type="number" value={formData.saldo_inicial} onChange={(e) => setFormData({...formData, saldo_inicial: parseFloat(e.target.value)})} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CuentasBancarias;
